import {
  TTranslation,
  useDynamicOptionsBuilder,
} from "@/features/FormCard/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { CategoryOption, DEFAULT_CATEGORY_OPTIONS } from "./config";
import {
  getFilteredCategoryOptions,
  isCashFormat,
  isCashAccount,
  shouldAutoSelectOption,
  shouldResetCategoryValue,
  AccountType,
} from "./utils";

/**
 * Hook to calculate the total fees from different fee types
 *
 * @param form - The form containing the fee values
 * @returns A function that calculates and updates the total fees only if the total has changed
 */
export function useFeesCalculator(form: UseFormReturn<any>) {
  return useCallback(() => {
    const brokerFee = form.watch("broker_fee") || 0;
    const custodianBrokerFee = form.watch("custodian_broker_fee") || 0;
    const otherFee = form.watch("other_fee") || 0;
    const currentFees = form.watch("fees") || 0;

    const totalFees =
      Number(brokerFee) + Number(custodianBrokerFee) + Number(otherFee);

    if (totalFees !== Number(currentFees)) {
      form.setValue("fees", totalFees, { shouldDirty: true });
    }
  }, [form]);
}

/**
 * Hook to manage category field state based on asset class and account type
 *
 * @param form Form instance
 * @returns State for category field (disabled state)
 */
export function useCategoryFieldState(
  form: UseFormReturn<any>,
  isLoadingData: boolean,
) {
  const [isCategoryDisabled, setIsCategoryDisabled] = useState(false);

  const prevAssetClass2Ref = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isLoadingData) return;

    const assetClass2 = form.watch("k2_static_datum_name")?.asset_class_2;

    // Disable category field if no asset class selected
    if (!assetClass2) {
      setIsCategoryDisabled(true);
      return;
    }

    // Enable category for all other cases
    setIsCategoryDisabled(false);

    if (shouldResetCategoryValue(prevAssetClass2Ref.current, assetClass2)) {
      form.setValue("category", null, { shouldDirty: true });
    }

    prevAssetClass2Ref.current = assetClass2;
  }, [
    form.watch("k2_static_datum_name")?.asset_class_2,
    form.watch("k2_portfolio_name")?.account_type,
    form,
    isLoadingData,
  ]);

  return { isCategoryDisabled };
}

/**
 * Hook to handle automatic selection of the single option
 *
 * @param options Available options
 * @param form Form instance
 * @param isCategoryDisabled If the category field is disabled
 * @param translations Optional translations for category values
 */
export function useAutoSelectSingleOption(
  options: any[],
  form: UseFormReturn<any>,
  isCategoryDisabled: boolean,
  translations?: TTranslation,
  keysAllowNullValue: string[] = [],
) {
  useEffect(() => {
    const currentCategory = form.watch("category") || "";
    const accountType = form.watch("k2_portfolio_name")?.account_type;
    const staticDatumName = form.watch("k2_static_datum_name")?.text;

    if (currentCategory) return;
    if (isCategoryDisabled)
      return form.setValue("category", null, { shouldDirty: true });

    if (isCashAccount(accountType) && isCashFormat(staticDatumName)) {
      return form.setValue("category", null, { shouldDirty: true });
    }

    const { shouldSelect, selectedValue } = shouldAutoSelectOption(
      options,
      currentCategory,
      isCategoryDisabled,
      keysAllowNullValue,
    );

    if (shouldSelect && selectedValue) {
      form.setValue("category", selectedValue, { shouldDirty: true });
    }
  }, [options, isCategoryDisabled, form, translations]);
}

/**
 * Hook to filter category options based on asset classes and account type
 *
 * @param form Form instance
 * @param translations Translations for options
 * @returns Filtered category options
 */
export function useCategoryOptions(
  form: UseFormReturn<FieldValues>,
  translations?: TTranslation,
) {
  const category_options = useDynamicOptionsBuilder({
    translation: translations?.["category"],
    options: DEFAULT_CATEGORY_OPTIONS,
    adjustOptions: (options) => {
      const assetClass2 = form.watch("k2_static_datum_name")?.asset_class_2;
      const accountType = form.watch("k2_portfolio_name")?.account_type;
      const staticDatumName = form.watch("k2_static_datum_name")?.text;
      const marketValueLocalCcy = form.watch("market_value_local_ccy");

      return getFilteredCategoryOptions(
        options as CategoryOption[],
        accountType as AccountType,
        assetClass2,
        staticDatumName,
        marketValueLocalCcy,
      );
    },
    deps: [
      translations,
      form.watch("k2_static_datum_name")?.asset_class_2,
      form.watch("k2_portfolio_name")?.account_type,
      form.watch("k2_static_datum_name")?.text,
      form.watch("market_value_local_ccy"),
    ],
  });

  return category_options;
}

/**
 * Hook to manage category auto-selection logic
 */
export function useCategoryAutoSelection({
  form,
  translations,
  keysAllowNullValue = [],
  isLoadingData = false,
}: {
  form: UseFormReturn<FieldValues>;
  translations?: TTranslation;
  keysAllowNullValue?: string[];
  isLoadingData: boolean;
}) {
  const { isCategoryDisabled } = useCategoryFieldState(form, isLoadingData);
  const category_options = useCategoryOptions(form, translations);

  useAutoSelectSingleOption(
    category_options.options,
    form,
    isCategoryDisabled,
    translations,
    keysAllowNullValue,
  );

  return {
    category_options,
    isCategoryDisabled,
  };
}
